import type React from "react";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import type { MetricCategory } from "@/components/metrics/types";
import { formatDate } from "@/lib/date-formatting";
import type { SalespersonMetrics } from "@/types/salesperson-types";

export const DRILLDOWN_LIMIT = 5000;

// Define which metrics are available for salespeople
export const SALESPERSON_METRICS = [
  "accounts_engaged",
  "total_engagements",
  "people_engaged",
  "opportunities_influenced",
  "opportunities_influenced_amount",
  "opportunities_influenced_won",
  "opportunities_influenced_won_amount",
  "opportunities_influenced_in_cycle",
  "opportunities_influenced_in_cycle_amount",
  "opportunities_created_post_engagement",
  "opportunities_created_post_engagement_amount",
  "deal_contacts_engaged",
  "accounts_activated",
  "accounts_reactivated",
  "accounts_without_opps",
  "salesperson_active_days",
  "engagements_per_account",
  "engagements_per_person",
  "engagements_per_deal_contact",
] as const;

export type SalespersonMetric = (typeof SALESPERSON_METRICS)[number];

// Define hidden metrics that should be fetched but not displayed
export const HIDDEN_METRICS: SalespersonMetric[] = [
  "opportunities_influenced_amount",
  "opportunities_influenced_in_cycle_amount",
  "opportunities_created_post_engagement_amount",
  "opportunities_influenced_won_amount",
  "engagements_per_account",
  "engagements_per_person",
  "engagements_per_deal_contact",
];

// Define WIP metrics that should be behind a feature flag
export const WIP_METRICS: SalespersonMetric[] = [];

// Define default metric order (matching Campaign pattern)
export const DEFAULT_METRIC_ORDER: SalespersonMetric[] = [
  "accounts_engaged",
  "total_engagements",
  "people_engaged",
  "deal_contacts_engaged",
  "opportunities_influenced",
  "opportunities_influenced_amount",
  "opportunities_influenced_won",
  "opportunities_influenced_won_amount",
  "opportunities_created_post_engagement",
  "opportunities_created_post_engagement_amount",
  "opportunities_influenced_in_cycle",
  "opportunities_influenced_in_cycle_amount",
  "accounts_activated",
  "accounts_reactivated",
  "accounts_without_opps",
  "salesperson_active_days",
];

// Render options type
interface RenderOptions {
  metricKey?: SalespersonMetric;
  metrics?: SalespersonMetrics;
  isBenchmark?: boolean;
  isCurrency?: boolean;
  amountMetricKey?: SalespersonMetric;
  secondaryMetricKey?: SalespersonMetric;
  secondaryValue?: number;
  secondaryValueLabel?: React.ReactNode;
}

// Default render function for metrics
export function renderMetric(
  value: number | null,
  options?: RenderOptions,
): React.ReactNode {
  // --- Primary Value Calculation ---
  const safeValue = value ?? 0;
  const formattedValue = options?.isCurrency
    ? safeValue.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      })
    : safeValue.toLocaleString("en-US");

  // --- Secondary Content Calculation ---
  let secondaryContentJsx: React.ReactNode = null;
  if (options) {
    const secondaryKey = options.amountMetricKey || options.secondaryMetricKey;
    const secondaryValue = secondaryKey
      ? options.metrics?.[secondaryKey]
      : options.secondaryValue;

    const formattedSecondaryValue =
      typeof secondaryValue === "number"
        ? (secondaryValue ?? 0).toLocaleString("en-US", {
            style: options.amountMetricKey ? "currency" : "decimal",
            currency: "USD",
            maximumFractionDigits: options.amountMetricKey ? 0 : 1,
          })
        : null;

    // CASE 1: Custom Label (passed via secondaryValueLabel without secondaryValue/Key)
    if (
      options.secondaryValueLabel &&
      !secondaryKey &&
      options.secondaryValue === undefined
    ) {
      secondaryContentJsx = (
        <span
          className="font-normal whitespace-nowrap"
          title={
            typeof options.secondaryValueLabel === "string"
              ? options.secondaryValueLabel
              : undefined
          }
        >
          {options.secondaryValueLabel}
        </span>
      );
    }
    // CASE 2: Numeric Secondary Value (Amount or Ratio)
    else if (secondaryKey || typeof options.secondaryValue === "number") {
      // Avoid rendering if primary is 0 for ratios/amounts
      if (
        !(
          (options.secondaryMetricKey || options.amountMetricKey) &&
          (value === null || value === 0)
        )
      ) {
        // Create tooltip text for the full secondary metric
        const tooltipText =
          formattedSecondaryValue &&
          options.secondaryValueLabel &&
          typeof options.secondaryValueLabel === "string"
            ? `${formattedSecondaryValue}${options.secondaryValueLabel}`
            : undefined;

        secondaryContentJsx = (
          <span className="inline-flex items-center gap-1" title={tooltipText}>
            {/* Display current secondary value */}
            <span>{formattedSecondaryValue}</span>

            {/* Display label */}
            {options.secondaryValueLabel &&
              typeof options.secondaryValueLabel === "string" && (
                <span className="font-normal whitespace-nowrap">
                  {options.secondaryValueLabel}
                </span>
              )}
          </span>
        );
      }
    }
  }

  // --- Final JSX Structure ---
  return (
    <div className="flex flex-col">
      {/* Row 1: Primary Value */}
      <div className="relative flex items-start">
        <span className="text-2xl sm:text-3xl md:text-2xl lg:text-3xl font-bold leading-none">
          {formattedValue}
        </span>
      </div>
      {/* Row 2: Secondary Content */}
      <div className="min-h-[20px] sm:min-h-[24px] md:min-h-[20px] lg:min-h-[24px] text-sm sm:text-base md:text-sm lg:text-base text-muted-foreground/50">
        {secondaryContentJsx}
      </div>
    </div>
  );
}

// Metric configuration with render functions
export const METRIC_CONFIG: Record<
  SalespersonMetric,
  {
    title: string;
    category: MetricCategory;
    tooltip?: string;
    format?: "number" | "currency" | "percentage" | "days";
    isWip?: boolean;
    drilldownType?: "table" | "chart";
    render?: (
      value: number | null,
      options: RenderOptions,
    ) => string | React.ReactNode;
  }
> = {
  accounts_engaged: {
    title: "Accounts Engaged",
    category: "Account",
    tooltip:
      "Count of accounts where any person engaged with this salesperson.",
    render: (value: number | null, options: RenderOptions) =>
      renderMetric(value, {
        secondaryValue:
          (options.metrics?.engagements_per_account as number) ?? 0,
        secondaryValueLabel: ` ${
          (options.metrics?.engagements_per_account as number) === 1
            ? "engagement"
            : "engagements"
        }/account`,
        secondaryMetricKey: "engagements_per_account",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
      }),
  },
  total_engagements: {
    title: "Total Engagements",
    category: "Touchpoint",
    tooltip:
      "Count of total engagements by this salesperson (may include more than one per person). Excludes any engagements from your own team.",
  },
  people_engaged: {
    title: "People Engaged",
    category: "Person",
    tooltip:
      "Count of people who engaged with this salesperson. Excludes any people from your own team.",
    render: (value: number | null, options: RenderOptions) =>
      renderMetric(value, {
        secondaryValue:
          (options.metrics?.engagements_per_person as number) ?? 0,
        secondaryValueLabel: ` ${
          (options.metrics?.engagements_per_person as number) === 1
            ? "engagement"
            : "engagements"
        }/person`,
        secondaryMetricKey: "engagements_per_person",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
      }),
  },
  opportunities_influenced: {
    title: "Opportunities Influenced",
    category: "Opportunity",
    tooltip:
      "Count and total amount of opportunities influenced by this salesperson.",
    render: (value: number | null, options: RenderOptions) =>
      renderMetric(value, {
        amountMetricKey: "opportunities_influenced_amount",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
      }),
  },
  opportunities_influenced_amount: {
    title: "Sum of Amounts for All Opportunities Influenced",
    category: "Opportunity",
    tooltip:
      "Sum of the total amount of all opportunities influenced by this salesperson.",
    format: "currency",
  },
  opportunities_influenced_won: {
    title: "Won Opportunities Influenced",
    category: "Opportunity",
    tooltip:
      "Count and total amount of won opportunities influenced by this salesperson.",
    render: (value: number | null, options: RenderOptions) =>
      renderMetric(value, {
        amountMetricKey: "opportunities_influenced_won_amount",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
      }),
  },
  opportunities_influenced_won_amount: {
    title: "Sum of Amounts for Won Opportunities Influenced",
    category: "Opportunity",
    tooltip:
      "Sum of the total amount of won opportunities influenced by this salesperson.",
    format: "currency",
  },
  opportunities_influenced_in_cycle: {
    title: "Opportunities Influenced In-Cycle",
    category: "Opportunity",
    tooltip:
      "Count and total amount of opportunities influenced by this salesperson during their active cycle.",
    render: (value: number | null, options: RenderOptions) =>
      renderMetric(value, {
        amountMetricKey: "opportunities_influenced_in_cycle_amount",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
      }),
  },
  opportunities_influenced_in_cycle_amount: {
    title: "Sum of Amounts for Opportunities Influenced In-Cycle",
    category: "Opportunity",
    tooltip:
      "Sum of the total amount of opportunities influenced by this salesperson during their active cycle.",
    format: "currency",
  },
  opportunities_created_post_engagement: {
    title: "Opportunities Created Post-Engagement",
    category: "Opportunity",
    tooltip: "Opportunities created after engagement by this salesperson.",
    render: (value: number | null, options: RenderOptions) =>
      renderMetric(value, {
        amountMetricKey: "opportunities_created_post_engagement_amount",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
      }),
  },
  opportunities_created_post_engagement_amount: {
    title: "Sum of Amounts for Opportunities Created Post-Engagement",
    category: "Opportunity",
    tooltip:
      "Sum of the total amount of opportunities that were created after engagement by this salesperson.",
    format: "currency",
  },
  deal_contacts_engaged: {
    title: "Deal Contacts Engaged",
    category: "Person",
    tooltip:
      "Count of people who engaged with this salesperson and have been in the buying group for any opportunity. Includes buying group associations via both CRM contact roles and Upside's healed buying groups. Excludes any people from your own team.",
    render: (value: number | null, options: RenderOptions) =>
      renderMetric(value, {
        secondaryValue:
          (options.metrics?.engagements_per_deal_contact as number) ?? 0,
        secondaryValueLabel: ` ${
          (options.metrics?.engagements_per_deal_contact as number) === 1
            ? "engagement"
            : "engagements"
        }/contact`,
        secondaryMetricKey: "engagements_per_deal_contact",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
      }),
  },
  accounts_activated: {
    title: "Accounts Activated",
    category: "Account",
    tooltip:
      "Count of accounts that had no engagement at any time prior to engagement with this salesperson.",
  },
  accounts_reactivated: {
    title: "Accounts reactivated",
    category: "Account",
    tooltip:
      "Count of accounts that had no engagement for 90 days prior to engagement with this salesperson.",
  },
  accounts_without_opps: {
    title: "Accounts Engaged Outside of Opportunity Cycle",
    category: "Account",
    tooltip:
      "Count of accounts where all engagements happened while no matching opportunity was open.",
  },
  salesperson_active_days: {
    title: "Active days",
    category: "Other",
    tooltip:
      "Number of days the salesperson was active, showing engagement activity over time.",
    drilldownType: "chart" as const,
    render: (_value: number | null, options: RenderOptions) => {
      // Calculate primary value and date range string
      const primaryValue = options.metrics?.salesperson_active_days ?? null;

      // Convert dates to strings if they are numbers
      const firstDate = options.metrics?.first_engagement_date;
      const lastDate = options.metrics?.last_engagement_date;

      const dateRangeStr =
        firstDate && lastDate
          ? `${formatDate(String(firstDate))} - ${formatDate(String(lastDate))}`
          : "No engagement dates";

      // Call renderMetric, passing date range as secondaryValueLabel
      return renderMetric(Number(primaryValue), {
        metricKey: options.metricKey,
        isBenchmark: options.isBenchmark,
        secondaryValueLabel: dateRangeStr, // Pass the date range here
      });
    },
  },
  engagements_per_account: {
    title: "Engagements per Account",
    category: "Account",
    tooltip: "Average number of engagements per account.",
    format: "number",
  },
  engagements_per_person: {
    title: "Engagements per Person",
    category: "Person",
    tooltip: "Average number of engagements per person.",
    format: "number",
  },
  engagements_per_deal_contact: {
    title: "Engagements per Deal Contact",
    category: "Person",
    tooltip: "Average number of engagements per deal contact.",
    format: "number",
  },
};

// Export category styles from campaign config
export { CATEGORY_STYLES };
